/**
 * ChainVault WP - Onboarding Component
 *
 * The first screen users see when they visit the wallet.
 * Allows users to:
 * 1. Create a brand new wallet with a 12-word seed phrase
 * 2. Restore an existing wallet from seed phrase
 * 3. Connect an existing MetaMask or Phantom wallet
 *
 * Private keys never leave the browser.
 * This is a fully non-custodial wallet implementation.
 *
 * @package ChainVault
 * @since   1.0.0
 */

import React, { useState, useRef } from 'react';
import { createWalletFromSeed, saveWalletToStorage } from '../utils/wallet';

/**
 * Full BIP39 English wordlist subset — 256 common words
 * Used for generating cryptographically random seed phrases
 */
const WORDLIST = [
    'abandon','ability','able','about','above','absent','absorb','abstract',
    'absurd','abuse','access','accident','account','accuse','achieve','acid',
    'acoustic','acquire','across','act','action','actor','actress','actual',
    'adapt','add','addict','address','adjust','admit','adult','advance',
    'advice','aerobic','afford','afraid','again','age','agent','agree',
    'ahead','aim','air','airport','aisle','alarm','album','alcohol',
    'alert','alien','all','alley','allow','almost','alone','alpha',
    'already','also','alter','always','amateur','amazing','among','amount',
    'amused','analyst','anchor','ancient','anger','angle','angry','animal',
    'ankle','announce','annual','another','answer','antenna','antique','anxiety',
    'any','apart','apology','appear','apple','approve','april','arch',
    'arctic','area','arena','argue','arm','armor','army','around',
    'arrange','arrest','arrive','arrow','art','artefact','artist','artwork',
    'ask','aspect','assault','asset','assist','assume','asthma','athlete',
    'atom','attack','attend','attitude','attract','auction','audit','august',
    'aunt','author','auto','autumn','average','avocado','avoid','awake',
    'aware','away','awesome','awful','awkward','axis','baby','balance',
    'bamboo','banana','banner','bar','barely','bargain','barrel','base',
    'basic','basket','battle','beach','bean','beauty','because','become',
    'beef','before','begin','behave','behind','believe','below','belt',
    'bench','benefit','best','betray','better','between','beyond','bicycle',
    'bid','bike','bind','biology','bird','birth','bitter','black',
    'blade','blame','blanket','blast','bleak','bless','blind','blood',
    'blossom','blouse','blue','blur','blush','board','boat','body',
    'boil','bomb','bone','book','boost','border','boring','borrow',
    'boss','bottom','bounce','box','boy','bracket','brain','brand',
    'brave','bread','breeze','brick','bridge','brief','bright','bring',
    'brisk','broccoli','broken','bronze','broom','brother','brown','brush',
    'bubble','buddy','budget','buffalo','build','bulb','bulk','bullet',
    'bundle','bunker','burden','burger','burst','bus','business','busy',
    'butter','buyer','buzz','cabbage','cabin','cable','cactus','cage',
    'cake','call','calm','camera','camp','canal','cancel','candy',
    'cannon','canvas','canyon','capable','capital','captain','carbon','card',
    'cargo','carpet','carry','cart','case','cash','casino','castle',
    'casual','catalog','catch','category','cattle','caught','cause','caution',
];

/**
 * Generate a cryptographically random 12-word seed phrase
 *
 * @return {string} 12-word seed phrase
 */
function generateMnemonic() {
    const array = new Uint32Array( 12 );
    crypto.getRandomValues( array );
    return Array.from( array )
        .map( n => WORDLIST[ n % WORDLIST.length ] )
        .join( ' ' );
}

/**
 * Connect Wallet Step
 */
function ConnectWalletStep( { onBack, onConnectMetaMask, onConnectPhantom } ) {
    return (
        <div className="cv-onboarding-step">

            <button className="cv-onboarding-back" onClick={ onBack }>
                ← Back
            </button>

            <div className="cv-onboarding-step-header">
                <span className="cv-onboarding-step-icon">🔗</span>
                <h2>Connect Your Wallet</h2>
                <p>
                    Choose your wallet to connect. Your existing wallet will be
                    used for all transactions.
                </p>
            </div>

            <div className="cv-onboarding-options">

                <button
                    className="cv-onboarding-option"
                    onClick={ onConnectMetaMask }
                >
                    <img
                        src="https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg"
                        alt="MetaMask"
                        style={{ width: '28px', height: '28px', borderRadius: '6px' }}
                    />
                    <div className="cv-onboarding-option-info">
                        <strong>MetaMask</strong>
                        <span>
                            { window.ethereum
                                ? 'Installed — Ethereum & EVM networks'
                                : 'Not installed — click to install'
                            }
                        </span>
                    </div>
                    <span className="cv-onboarding-option-arrow">›</span>
                </button>

                <button
                    className="cv-onboarding-option"
                    onClick={ onConnectPhantom }
                >
                    <img
                        src="https://assets.coingecko.com/coins/images/4128/small/solana.png"
                        alt="Phantom"
                        style={{ width: '28px', height: '28px', borderRadius: '6px' }}
                    />
                    <div className="cv-onboarding-option-info">
                        <strong>Phantom</strong>
                        <span>
                            { window?.solana?.isPhantom
                                ? 'Installed — Solana wallet'
                                : 'Not installed — click to install'
                            }
                        </span>
                    </div>
                    <span className="cv-onboarding-option-arrow">›</span>
                </button>

                <button className="cv-onboarding-option">
                    <span style={{ fontSize: '24px' }}>🔗</span>
                    <div className="cv-onboarding-option-info">
                        <strong>WalletConnect</strong>
                        <span>550+ wallets — Coming soon</span>
                    </div>
                    <span className="cv-onboarding-option-arrow">›</span>
                </button>

            </div>

        </div>
    );
}

/**
 * Step 1 — Welcome Screen
 */
function WelcomeStep( { onCreateNew, onRestore, onConnect } ) {
    return (
        <div className="cv-onboarding-welcome">

            <div className="cv-onboarding-logo">
                <span className="cv-onboarding-logo-icon">⛓</span>
                <span className="cv-onboarding-logo-text">ChainVault</span>
            </div>

            <h1 className="cv-onboarding-title">Your Multi-Chain Wallet</h1>
            <p className="cv-onboarding-subtitle">
                The most powerful crypto wallet for WordPress.
                Create a new wallet or connect your existing one.
            </p>

            <div className="cv-onboarding-options">

                <button
                    className="cv-onboarding-option primary"
                    onClick={ onCreateNew }
                >
                    <span className="cv-onboarding-option-icon">✨</span>
                    <div className="cv-onboarding-option-info">
                        <strong>Create New Wallet</strong>
                        <span>Generate a new wallet with a secret phrase</span>
                    </div>
                    <span className="cv-onboarding-option-arrow">›</span>
                </button>

                <button
                    className="cv-onboarding-option"
                    onClick={ onRestore }
                >
                    <span className="cv-onboarding-option-icon">🔄</span>
                    <div className="cv-onboarding-option-info">
                        <strong>Restore from Seed Phrase</strong>
                        <span>Import an existing wallet using 12 words</span>
                    </div>
                    <span className="cv-onboarding-option-arrow">›</span>
                </button>

                <button
                    className="cv-onboarding-option"
                    onClick={ onConnect }
                >
                    <span className="cv-onboarding-option-icon">🔗</span>
                    <div className="cv-onboarding-option-info">
                        <strong>Connect Existing Wallet</strong>
                        <span>Connect MetaMask, Phantom or WalletConnect</span>
                    </div>
                    <span className="cv-onboarding-option-arrow">›</span>
                </button>

            </div>

            <div className="cv-onboarding-security-note">
                <span>🔒</span>
                <p>
                    ChainVault is fully non-custodial. Your private keys are stored
                    only in your browser and never sent to any server.
                </p>
            </div>

        </div>
    );
}

/**
 * Step 2 — Show Seed Phrase
 */
function GenerateSeedStep( { mnemonic, onNext, onBack } ) {

    const [ confirmed, setConfirmed ] = useState( false );
    const [ hidden,    setHidden    ] = useState( true );
    const words = mnemonic.trim().split( ' ' );

    return (
        <div className="cv-onboarding-step">

            <button className="cv-onboarding-back" onClick={ onBack }>
                ← Back
            </button>

            <div className="cv-onboarding-step-header">
                <span className="cv-onboarding-step-icon">🔑</span>
                <h2>Your Secret Phrase</h2>
                <p>
                    Write these 12 words down in order and store them safely.
                    This is the ONLY way to recover your wallet.
                </p>
            </div>

            <div className="cv-warning-box">
                ⚠️ Never share your seed phrase. Anyone with these words can steal all your crypto.
            </div>

            <button
                className="cv-onboarding-btn"
                style={{
                    background: 'transparent',
                    border: '1px solid var(--cv-border)',
                    color: 'var(--cv-text-secondary)',
                    fontSize: '13px',
                    padding: '8px 16px',
                    width: 'auto',
                    alignSelf: 'center',
                }}
                onClick={ () => setHidden( h => ! h ) }
            >
                { hidden ? '👁 Show Seed Phrase' : '🙈 Hide Seed Phrase' }
            </button>

            <div className="cv-seed-grid">
                { words.map( ( word, index ) => (
                    <div key={ index } className="cv-seed-word">
                        <span className="cv-seed-number">{ index + 1 }</span>
                        <span className="cv-seed-text">
                            { hidden ? '••••••' : word }
                        </span>
                    </div>
                ) ) }
            </div>

            <label className="cv-onboarding-checkbox">
                <input
                    type="checkbox"
                    checked={ confirmed }
                    onChange={ e => setConfirmed( e.target.checked ) }
                />
                <span>
                    I have written down my 12-word seed phrase in a safe place.
                    I understand that if I lose it I cannot recover my wallet.
                </span>
            </label>

            <button
                className="cv-onboarding-btn primary"
                onClick={ onNext }
                disabled={ ! confirmed }
            >
                Continue →
            </button>

        </div>
    );
}

/**
 * Step 3 — Confirm Seed Phrase
 * User fills in 12 numbered boxes — one word per box
 * No auto-advance — user types freely and presses Tab or clicks next box
 */
function ConfirmSeedStep( { mnemonic, onNext, onBack } ) {

    const correctWords = mnemonic.trim().split( ' ' );
    const [ inputs,  setInputs  ] = useState( Array( 12 ).fill( '' ) );
    const [ error,   setError   ] = useState( '' );
    const [ loading, setLoading ] = useState( false );
    const inputRefs = useRef( [] );

    /**
     * Update a single word input — no auto-advance
     * User types the full word and moves with Tab or click
     */
    const handleInput = ( index, value ) => {
        const cleaned = value.toLowerCase().replace( /[^a-z]/g, '' );
        const next    = [ ...inputs ];
        next[ index ] = cleaned;
        setInputs( next );
        setError( '' );
    };

    /**
     * Handle paste — splits pasted text across all boxes
     * If user pastes all 12 words at once it fills everything automatically
     */
    const handlePaste = ( e, startIndex ) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData( 'text' )
            .trim()
            .toLowerCase()
            .split( /\s+/ );

        if ( pasted.length > 1 ) {
            const next = [ ...inputs ];
            pasted.forEach( ( word, i ) => {
                if ( startIndex + i < 12 ) {
                    next[ startIndex + i ] = word.replace( /[^a-z]/g, '' );
                }
            } );
            setInputs( next );
        }
    };

    /**
     * Verify inputs match the original seed phrase word by word
     */
    const handleConfirm = async () => {
        const entered = inputs.map( w => w.trim().toLowerCase() );

        if ( entered.some( w => ! w ) ) {
            setError( 'Please fill in all 12 words.' );
            return;
        }

        for ( let i = 0; i < 12; i++ ) {
            if ( entered[ i ] !== correctWords[ i ].toLowerCase() ) {
                setError( `Word ${ i + 1 } is incorrect. Please check and try again.` );
                inputRefs.current[ i ]?.focus();
                return;
            }
        }

        setError( '' );
        setLoading( true );

        try {
            const walletData = await createWalletFromSeed( mnemonic );
            saveWalletToStorage( walletData );
            onNext( walletData );
        } catch ( err ) {
            console.error( 'Wallet creation error:', err );
            setError( 'Failed to create wallet. Please try again.' );
        } finally {
            setLoading( false );
        }
    };

    return (
        <div className="cv-onboarding-step">

            <button className="cv-onboarding-back" onClick={ onBack }>
                ← Back
            </button>

            <div className="cv-onboarding-step-header">
                <span className="cv-onboarding-step-icon">✅</span>
                <h2>Confirm Your Seed Phrase</h2>
                <p>
                    Type each word into the correct numbered box.
                    Use Tab to move between boxes or click each one.
                </p>
            </div>

            <div className="cv-seed-confirm-grid">
                { inputs.map( ( val, index ) => (
                    <div key={ index } className="cv-seed-confirm-box">
                        <span className="cv-seed-confirm-number">{ index + 1 }</span>
                        <input
                            ref={ el => inputRefs.current[ index ] = el }
                            type="text"
                            className="cv-seed-confirm-input"
                            value={ val }
                            onChange={ e => handleInput( index, e.target.value ) }
                            onPaste={ e => handlePaste( e, index ) }
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={ false }
                            placeholder={ `Word ${ index + 1 }` }
                        />
                    </div>
                ) ) }
            </div>

            { error && (
                <div className="cv-onboarding-error">
                    ❌ { error }
                </div>
            ) }

            <button
                className="cv-onboarding-btn primary"
                onClick={ handleConfirm }
                disabled={ inputs.some( w => ! w.trim() ) || loading }
            >
                { loading ? 'Creating wallet...' : 'Create Wallet →' }
            </button>

        </div>
    );
}

/**
 * Step — Restore from Seed Phrase
 * User fills in 12 numbered boxes to restore their wallet
 * No auto-advance — user types freely
 */
function RestoreStep( { onNext, onBack } ) {

    const [ inputs,  setInputs  ] = useState( Array( 12 ).fill( '' ) );
    const [ error,   setError   ] = useState( '' );
    const [ loading, setLoading ] = useState( false );
    const inputRefs = useRef( [] );

    /**
     * Update a single word input — no auto-advance
     */
    const handleInput = ( index, value ) => {
        const cleaned = value.toLowerCase().replace( /[^a-z]/g, '' );
        const next    = [ ...inputs ];
        next[ index ] = cleaned;
        setInputs( next );
        setError( '' );
    };

    /**
     * Handle paste — splits pasted text across all 12 boxes automatically
     */
    const handlePaste = ( e, startIndex ) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData( 'text' )
            .trim()
            .toLowerCase()
            .split( /\s+/ );

        if ( pasted.length > 1 ) {
            const next = [ ...inputs ];
            pasted.forEach( ( word, i ) => {
                if ( startIndex + i < 12 ) {
                    next[ startIndex + i ] = word.replace( /[^a-z]/g, '' );
                }
            } );
            setInputs( next );
        }
    };

    const handleRestore = async () => {
        const filled = inputs.map( w => w.trim().toLowerCase() );

        if ( filled.some( w => ! w ) ) {
            setError( 'Please fill in all 12 words.' );
            return;
        }

        const phrase = filled.join( ' ' );
        setError( '' );
        setLoading( true );

        try {
            const walletData = await createWalletFromSeed( phrase );
            saveWalletToStorage( walletData );
            onNext( walletData );
        } catch ( err ) {
            console.error( 'Wallet restore error:', err );
            setError( 'Failed to restore wallet. Please check your words and try again.' );
        } finally {
            setLoading( false );
        }
    };

    return (
        <div className="cv-onboarding-step">

            <button className="cv-onboarding-back" onClick={ onBack }>
                ← Back
            </button>

            <div className="cv-onboarding-step-header">
                <span className="cv-onboarding-step-icon">🔄</span>
                <h2>Restore Your Wallet</h2>
                <p>
                    Type each seed word into the correct numbered box.
                    You can also paste all 12 words into box 1 and they will fill automatically.
                </p>
            </div>

            <div className="cv-warning-box">
                ⚠️ Only enter your seed phrase on a device you trust. Never share it with anyone.
            </div>

            <div className="cv-seed-confirm-grid">
                { inputs.map( ( val, index ) => (
                    <div key={ index } className="cv-seed-confirm-box">
                        <span className="cv-seed-confirm-number">{ index + 1 }</span>
                        <input
                            ref={ el => inputRefs.current[ index ] = el }
                            type="text"
                            className="cv-seed-confirm-input"
                            value={ val }
                            onChange={ e => handleInput( index, e.target.value ) }
                            onPaste={ e => handlePaste( e, index ) }
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={ false }
                            placeholder={ `Word ${ index + 1 }` }
                        />
                    </div>
                ) ) }
            </div>

            { error && (
                <div className="cv-onboarding-error">
                    ❌ { error }
                </div>
            ) }

            <button
                className="cv-onboarding-btn primary"
                onClick={ handleRestore }
                disabled={ inputs.some( w => ! w.trim() ) || loading }
            >
                { loading ? 'Restoring wallet...' : 'Restore Wallet →' }
            </button>

        </div>
    );
}

/**
 * Step — Wallet Created Successfully
 */
function SuccessStep( { walletData, onFinish } ) {
    return (
        <div className="cv-onboarding-step">

            <div className="cv-onboarding-success">
                <div className="cv-onboarding-success-icon">🎉</div>
                <h2>Wallet Created!</h2>
                <p>Your wallet is ready. Here are your wallet addresses:</p>
            </div>

            <div className="cv-wallet-addresses">

                <div className="cv-wallet-address-row">
                    <div className="cv-wallet-address-info">
                        <img
                            src="https://assets.coingecko.com/coins/images/1/small/bitcoin.png"
                            alt="BTC"
                            className="cv-wallet-address-icon"
                        />
                        <div>
                            <span className="cv-wallet-address-label">Bitcoin</span>
                            <span className="cv-wallet-address-value">
                                { walletData.bitcoin.address.slice( 0, 10 ) }...{ walletData.bitcoin.address.slice( -6 ) }
                            </span>
                        </div>
                    </div>
                    <button
                        className="cv-copy-btn"
                        onClick={ () => navigator.clipboard.writeText( walletData.bitcoin.address ) }
                    >
                        Copy
                    </button>
                </div>

                <div className="cv-wallet-address-row">
                    <div className="cv-wallet-address-info">
                        <img
                            src="https://assets.coingecko.com/coins/images/279/small/ethereum.png"
                            alt="ETH"
                            className="cv-wallet-address-icon"
                        />
                        <div>
                            <span className="cv-wallet-address-label">Ethereum</span>
                            <span className="cv-wallet-address-value">
                                { walletData.ethereum.address.slice( 0, 10 ) }...{ walletData.ethereum.address.slice( -6 ) }
                            </span>
                        </div>
                    </div>
                    <button
                        className="cv-copy-btn"
                        onClick={ () => navigator.clipboard.writeText( walletData.ethereum.address ) }
                    >
                        Copy
                    </button>
                </div>

                <div className="cv-wallet-address-row">
                    <div className="cv-wallet-address-info">
                        <img
                            src="https://assets.coingecko.com/coins/images/4128/small/solana.png"
                            alt="SOL"
                            className="cv-wallet-address-icon"
                        />
                        <div>
                            <span className="cv-wallet-address-label">Solana</span>
                            <span className="cv-wallet-address-value">
                                { walletData.solana.address.slice( 0, 10 ) }...{ walletData.solana.address.slice( -6 ) }
                            </span>
                        </div>
                    </div>
                    <button
                        className="cv-copy-btn"
                        onClick={ () => navigator.clipboard.writeText( walletData.solana.address ) }
                    >
                        Copy
                    </button>
                </div>

            </div>

            <div className="cv-onboarding-security-note">
                <span>🔒</span>
                <p>
                    Your wallet is saved securely in your browser.
                    Use the backup button in the wallet to view your seed phrase anytime.
                </p>
            </div>

            <button
                className="cv-onboarding-btn primary"
                onClick={ onFinish }
            >
                Open My Wallet →
            </button>

        </div>
    );
}

/**
 * Main Onboarding Component
 */
function Onboarding( { onComplete, onConnect, connectMetaMask, connectPhantomWallet } ) {

    const [ step,       setStep       ] = useState( 'welcome' );
    const [ mnemonic,   setMnemonic   ] = useState( '' );
    const [ walletData, setWalletData ] = useState( null );
    const [ error,      setError      ] = useState( '' );

    const handleCreateNew = () => {
        try {
            setError( '' );
            const newMnemonic = generateMnemonic();
            setMnemonic( newMnemonic );
            setStep( 'generate' );
        } catch ( err ) {
            setError( 'Failed to generate wallet. Please try again.' );
        }
    };

    const handleWalletCreated = ( data ) => {
        setWalletData( data );
        setStep( 'success' );
    };

    const handleConnectMetaMask = async () => {
        if ( connectMetaMask ) await connectMetaMask();
        else onConnect();
    };

    const handleConnectPhantom = async () => {
        if ( connectPhantomWallet ) await connectPhantomWallet();
        else onConnect();
    };

    const renderStep = () => {
        switch ( step ) {
            case 'welcome':
                return (
                    <WelcomeStep
                        onCreateNew={ handleCreateNew }
                        onRestore={ () => setStep( 'restore' ) }
                        onConnect={ () => setStep( 'connect' ) }
                    />
                );
            case 'generate':
                return (
                    <GenerateSeedStep
                        mnemonic={ mnemonic }
                        onNext={ () => setStep( 'confirm' ) }
                        onBack={ () => setStep( 'welcome' ) }
                    />
                );
            case 'confirm':
                return (
                    <ConfirmSeedStep
                        mnemonic={ mnemonic }
                        onNext={ handleWalletCreated }
                        onBack={ () => setStep( 'generate' ) }
                    />
                );
            case 'restore':
                return (
                    <RestoreStep
                        onNext={ handleWalletCreated }
                        onBack={ () => setStep( 'welcome' ) }
                    />
                );
            case 'connect':
                return (
                    <ConnectWalletStep
                        onBack={ () => setStep( 'welcome' ) }
                        onConnectMetaMask={ handleConnectMetaMask }
                        onConnectPhantom={ handleConnectPhantom }
                    />
                );
            case 'success':
                return (
                    <SuccessStep
                        walletData={ walletData }
                        onFinish={ () => onComplete( walletData ) }
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="cv-onboarding-container">
            <div className="cv-onboarding-card">
                { error && (
                    <div className="cv-onboarding-error" style={{ marginBottom: '16px' }}>
                        ❌ { error }
                    </div>
                ) }
                { renderStep() }
            </div>
        </div>
    );
}

export default Onboarding;